// components/common/Navbar.jsx
import React, { useState, useEffect, lazy, Suspense } from 'react';
import NotificationBell from '../notifications/NotificationBell';
import { QrCode } from 'lucide-react';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';
import { safeSessionStorage } from '../../utils/safeStorage';
import { apiService } from '../../services/api';
import { isDataConstrained, prefersReducedMotion, watchConnection } from '../../utils/connection';
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, Shield, Sparkles, Headset, ShoppingBag, Gift, LayoutTemplate, BarChart3, CreditCard, IndianRupee, User, Star, ClipboardList, Tag, Users, FileInput, Bot, LifeBuoy } from 'lucide-react';
import { helpUrl } from '../../utils/help';
import ThemeSwitch from '../settings/ThemeSwitch';

// The animated GIF is 330KB against the static logo's 78KB, and loops forever
// with no way for a user to opt out — a GIF can't observe prefers-reduced-motion
// the way a CSS or video animation can. This mounts on every page except admin,
// storefronts and the native app shell (see App.jsx), so it's the single
// highest-frequency image load on the site. Same fallback logic already used
// for the page-builder video background (src/utils/connection.js): show the
// static logo on Data Saver / 2G / 3G connections or when the OS-level reduce-
// motion preference is on, animated otherwise.
const pickLogoSrc = () => (
  (prefersReducedMotion() || isDataConstrained()) ? '/logo.png' : '/Mintlink_logo_animation.gif'
);

// Lazy, and mounted only while actually open. The navbar renders on every
// page including public checkout and creator pages, and this modal pulls in
// qrcode + qrcode.react (~115kB) — which used to land in the entry bundle
// that every first-time visitor downloads, to draw a QR code that only a
// signed-in creator can ever open.
const ShareProfileModal = lazy(() => import(
  /* webpackChunkName: "share-modal" */ '../share/ShareProfileModal'
));

// Account admin links. These also live in a "Settings & tools" row at the bottom of
// the creator dashboard, where they're easy to miss — keep the two lists in sync.
const accountLinks = [
  { to: '/analytics',     icon: BarChart3,     label: 'Analytics' },
  { to: '/discount-codes', icon: Tag,          label: 'Discount codes' },
  { to: '/bank-details',  icon: CreditCard,    label: 'Bank account' },
  { to: '/transactions',  icon: IndianRupee,   label: 'Money history' },
  { to: '/profile',       icon: User,          label: 'My profile' },
  { to: '/reviews',       icon: Star,          label: 'Ratings' },
  { to: '/review-orders', icon: ClipboardList, label: 'Review orders' },
  { to: '/connected-apps', icon: Bot,          label: 'AI apps' },
];

const Navbar = () => {
  const { isAuthenticated, user, isHydrated, isAdmin,profile } = useSelector((state) => state.auth);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [shareOpen, setShareOpen] = useState(false);
  const [buyerLoggedIn, setBuyerLoggedIn] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Lazy initializer so the very first render already picks the right asset —
  // no flash of the animated GIF before a slow-connection check downgrades it.
  const [logoSrc, setLogoSrc] = useState(pickLogoSrc);
  const reducedMotion = prefersReducedMotion();

  // Re-evaluate if the connection changes (e.g. wifi → cellular). Not watched
  // when reduced-motion is already on, since that preference doesn't need a
  // network signal to override it.
  useEffect(() => {
    if (reducedMotion) return;
    return watchConnection(() => setLogoSrc(pickLogoSrc()));
  }, [reducedMotion]);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setBuyerLoggedIn(!!safeSessionStorage.getItem('buyer_token'));
  }, [location.pathname]);

  // Badge on the "Contact Support" links — how many of the creator's own
  // tickets have an admin reply they haven't seen yet.
  //
  // Polls on the same 2-minute cadence AppTabBar and AdminLayout use for their
  // badges, and only while the tab is visible. At the previous 30s with no
  // visibility check, a single tab left open in the background asked for this
  // count 2,880 times a day; it and the notification bell together were the
  // two busiest endpoints on the platform (~7.4k and ~8.0k calls/day). A
  // support reply does not need to surface within 30 seconds — and the
  // focus listener below means it appears the instant someone looks anyway.
  useEffect(() => {
    if (!isAuthenticated) { setSupportUnread(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const data = await apiService.getSupportUnreadCount();
        if (!cancelled) setSupportUnread(data.unread_count || 0);
      } catch { /* not fatal — badge just stays at its last known count */ }
    };
    // No timer — fetched on mount and whenever the tab is returned to. See
    // NotificationBell for the reasoning: an unread count nobody is looking at
    // does not need to be correct, and the visibility listener refreshes it
    // before anyone can look.
    fetchUnread();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchUnread(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated]);

  // Close mobile menus when authentication state changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDrawerOpen(false);
    setUserMenuOpen(false);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    setMobileDrawerOpen(false);
    setUserMenuOpen(false);
  };

  const handleNavigation = () => {
    setMobileMenuOpen(false);
    setMobileDrawerOpen(false);
  };

  const isBuyerPage = location.pathname.startsWith('/buyer');
  const homeTarget = !isAuthenticated ? '/' : isAdmin ? '/admin' : '/dashboard';

  const navLinks = [
    { href: '/how-it-works', label: 'How It Works' },
    { href: '/payments', label: 'Pricing' },
    { href: '/faq', label: 'FAQ' },
    { href: '/terms', label: 'Terms' },
    { href: '/privacy-policy', label: 'Privacy Policy' }
  ];

  return (
    <>
    <nav 
      // pt-navbar-y (not `py-3 pt-safe-t`) — the notch inset is folded into one
      // calc so it ADDS to the 0.75rem instead of replacing it. See tailwind.config.
      className={`w-full px-4 sm:px-6 lg:px-12 pb-3 pt-navbar-y flex justify-between items-center fixed top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-200' 
          : 'bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 relative z-50 shrink-0">
        <Link to={homeTarget} className="flex items-center gap-2 group">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#32764A] to-[#6BB170] rounded-xl blur-md opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
            <img
              src={logoSrc}
              alt="MintLink Logo"
              className="h-9 w-9 md:h-10 md:w-10 object-contain relative z-10 transform group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-xl md:text-2xl font-extrabold tracking-tight bg-gradient-to-r from-[#115435] to-[#32764A] dark:from-[#93d6aa] dark:to-[#6cc08a] bg-clip-text text-transparent">
              MintLink 
            </span>
            <span className="text-[10px] md:text-xs text-gray-500 font-medium">
              Your work deserves more than likes. 
            </span>
          </div>
        </Link>
      </div>

      {/* Center links (Desktop - public only) */}
      {!isAuthenticated && (
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="relative text-sm font-semibold text-gray-700 hover:text-[#32764A] transition-colors duration-200 group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#32764A] to-[#6BB170] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </div>
      )}

      {/* Right section */}
      <div className="flex items-center gap-3 sm:gap-4 relative z-50">
        {!isHydrated ? (
          <div className="w-8 h-8 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse rounded-full" />
        ) : isAuthenticated ? (
          <>
            {/* Desktop Auth Menu */}
            <div className="hidden md:flex items-center gap-3 relative z-50">
              <a
                href={helpUrl()}
                target="_blank"
                rel="noopener"
                title="Help Centre: guides with screenshots"
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 hover:text-[#32764A] hover:bg-gray-50 rounded-lg transition-colors"
              >
                <LifeBuoy className="w-4 h-4" />
                <span className="hidden lg:inline">Help</span>
              </a>
              <Link
                to="/support"
                className="relative flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 hover:text-[#32764A] hover:bg-gray-50 rounded-lg transition-colors"
              >
                <span className="relative">
                  <Headset className="w-4 h-4" />
                  {supportUnread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                      {supportUnread > 9 ? '9+' : supportUnread}
                    </span>
                  )}
                </span>
                <span className="hidden lg:inline">Contact Support</span>
              </Link>

              <NotificationBell />
              {!isBuyerPage && (
                <button
                  onClick={() => setShareOpen(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <QrCode className="w-4 h-4" />
                  Share Profile
                </button>
              )}

              {/* Admin Button */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="relative z-50 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-gray-700 bg-white hover:text-[#32764A] hover:bg-gray-50 border border-gray-200 shadow-sm transition-all duration-200"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden lg:inline">Admin</span>
                </button>
              )}

              {/* User Menu Dropdown */}
              <div className="relative z-50">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="relative z-50 flex items-center gap-3 px-4 py-2 rounded-full hover:bg-gray-50 transition-all duration-200 group"
                >
                  <div className="hidden lg:block text-right">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-[#32764A] transition-colors">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#32764A] to-[#6BB170] flex items-center justify-center text-white font-bold text-sm shadow-md group-hover:shadow-lg transition-shadow">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-[60]" 
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-60 max-h-[calc(100vh-5rem)] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-[70]">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        {isAdmin && (
                          <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-gradient-to-r from-[#32764A] to-[#6BB170] text-white text-xs font-semibold rounded-full">
                            <Sparkles className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </div>
                      <Link
                        to="/pages"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <LayoutTemplate className="w-4 h-4 text-[#32764A]" />
                        My Pages
                      </Link>
                      <Link
                        to="/community"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Users className="w-4 h-4 text-[#32764A]" />
                        Community
                      </Link>
                      <Link
                        to="/referrals"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Gift className="w-4 h-4 text-[#32764A]" />
                        Referral Program
                      </Link>
                      <Link
                        to="/forms"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <FileInput className="w-4 h-4 text-[#32764A]" />
                        MintLink Forms
                      </Link>

                      <div className="mt-1 pt-2 border-t border-gray-100">
                        <p className="px-4 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                          Settings &amp; tools
                        </p>
                        {accountLinks.map(({ to, icon: Icon, label }) => (
                          <Link
                            key={to}
                            to={to}
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Icon className="w-4 h-4 text-[#32764A]" />
                            {label}
                          </Link>
                        ))}
                      </div>

                      <div className="px-4 pt-2 pb-2 border-t border-gray-100">
                        <p className="pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                          Appearance
                        </p>
                        <ThemeSwitch compact />
                      </div>

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 mt-1 border-t border-gray-100 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Right Icons */}
            <div className="md:hidden flex items-center gap-3 shrink-0">
              <NotificationBell />
              <button
                onClick={() => setMobileDrawerOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
            </div>
          </>
        ) : (
          <>
            {/* My Purchases — desktop, shown when buyer is logged in */}
            {buyerLoggedIn && (
              <Link
                to="/buyer/dashboard"
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-[#32764A] border border-[#32764A]/30 hover:bg-[#32764A]/5 transition-all duration-200"
              >
                <ShoppingBag className="w-4 h-4" />
                My Purchases
              </Link>
            )}

            {/* Desktop Login Button */}
            <Link
              to="/login"
              className="hidden sm:flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white shadow-md hover:shadow-xl transition-all duration-200 bg-gradient-to-r from-[#32764A] to-[#6BB170] hover:scale-105"
            >
              <span>Login</span>
              <ChevronDown className="w-4 h-4 -rotate-90" />
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </>
        )}
      </div>

      {/* Mobile Menu (Public Only) */}
      {!isAuthenticated && mobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-xl sm:hidden z-40">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={handleNavigation}
                className="block py-2 text-base font-semibold text-gray-700 hover:text-[#32764A] transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {buyerLoggedIn && (
              <Link
                to="/buyer/dashboard"
                onClick={handleNavigation}
                className="flex items-center gap-2 py-2 text-base font-semibold text-[#32764A] transition-colors"
              >
                <ShoppingBag className="w-5 h-5" />
                My Purchases
              </Link>
            )}
            <Link
              to="/login"
              onClick={handleNavigation}
              className="block w-full text-center px-6 py-3 rounded-full text-sm font-semibold text-white shadow-md bg-gradient-to-r from-[#32764A] to-[#6BB170]"
            >
              Login
            </Link>
          </div>
        </div>
      )}

      {/* Mobile Drawer (Authenticated Only) */}
      {mobileDrawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 z-100"
            onClick={() => setMobileDrawerOpen(false)}
          />

          {/* Drawer */}
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-xl sm:hidden z-40 flex flex-col max-h-[calc(100svh-64px)] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b">
              <span className="font-semibold text-lg">Menu</span>
              <button 
                onClick={() => setMobileDrawerOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            {/* Profile */}
            <div className="px-4 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#32764A] to-[#6BB170] flex items-center justify-center text-white font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="flex-1 px-4 py-4 space-y-2">
              <Link
                to={isAdmin ? '/admin' : '/dashboard'}
                onClick={handleNavigation}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-shrink-0">
                  {isAdmin ? <Shield className="w-5 h-5" /> : <LayoutDashboard className="w-5 h-5" />}
                </div>
                <span className="text-sm font-medium">Dashboard</span>
              </Link>

              <Link
                to="/pages"
                onClick={handleNavigation}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#32764A]/5 transition-colors"
              >
                <div className="flex-shrink-0">
                  <LayoutTemplate className="w-5 h-5 text-[#32764A]" />
                </div>
                <span className="text-sm font-medium text-[#32764A] font-semibold">My Pages</span>
              </Link>

              <Link
                to="/community"
                onClick={handleNavigation}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#32764A]/5 transition-colors"
              >
                <div className="flex-shrink-0">
                  <Users className="w-5 h-5 text-[#32764A]" />
                </div>
                <span className="text-sm font-medium text-[#32764A] font-semibold">Community</span>
              </Link>

              <Link
                to="/referrals"
                onClick={handleNavigation}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#32764A]/5 transition-colors"
              >
                <div className="flex-shrink-0">
                  <Gift className="w-5 h-5 text-[#32764A]" />
                </div>
                <span className="text-sm font-medium text-[#32764A] font-semibold">Referral Program</span>
              </Link>

              <Link
                to="/forms"
                onClick={handleNavigation}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#32764A]/5 transition-colors"
              >
                <div className="flex-shrink-0">
                  <FileInput className="w-5 h-5 text-[#32764A]" />
                </div>
                <span className="text-sm font-medium text-gray-700">MintLink Forms</span>
              </Link>

              <a
                href={helpUrl()}
                target="_blank"
                rel="noopener"
                onClick={handleNavigation}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-shrink-0">
                  <LifeBuoy className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium flex-1">Help Centre</span>
              </a>

              <Link
                to="/support"
                onClick={handleNavigation}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="relative flex-shrink-0">
                  <Headset className="w-5 h-5" />
                  {supportUnread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                      {supportUnread > 9 ? '9+' : supportUnread}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium flex-1">Contact Support</span>
                {supportUnread > 0 && (
                  <span className="text-xs font-bold text-red-600">{supportUnread} new</span>
                )}
              </Link>
              {!isBuyerPage && (
                <button
                  onClick={() => setShareOpen(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <QrCode className="w-4 h-4" />
                  Share Profile
                </button>
              )}

              <div className="pt-3 mt-3 border-t border-gray-100">
                <p className="px-3 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Settings &amp; tools
                </p>
                {accountLinks.map(({ to, icon: Icon, label }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={handleNavigation}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#32764A]" />
                    </div>
                    <span className="text-sm font-medium">{label}</span>
                  </Link>
                ))}
              </div>

              <div className="pt-3 mt-3 border-t border-gray-100 px-3">
                <p className="pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Appearance
                </p>
                <ThemeSwitch compact />
              </div>
            </div>

            {/* Logout */}
            <div className="px-4 py-4 border-t">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              >
                <div className="flex-shrink-0">
                  <LogOut className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </>
      )}


    </nav>
          {/* Gated on shareOpen so the chunk is only fetched on first open —
              rendering it unconditionally would download it on every page. */}
          {shareOpen && (
            <Suspense fallback={null}>
              <ShareProfileModal
                isOpen={shareOpen}
                onClose={() => setShareOpen(false)}
                creatorId={profile?.creator_id}
                username={profile?.username}
                profileImage={profile?.profile_image}
              />
            </Suspense>
          )}
</>
  );
};

export default Navbar;
